import type { NormalizedCombination } from "@guandan/game-core";
import type { AuthorityEvent } from "./table-controller";

export interface SeatPlay {
  readonly seat: number;
  readonly cardIds: readonly string[];
  readonly combination: NormalizedCombination;
}

export interface VisibleSeatPlays {
  readonly plays: ReadonlyMap<number, SeatPlay>;
  readonly clearOnNextPlay: boolean;
}

export const EMPTY_VISIBLE_SEAT_PLAYS: VisibleSeatPlays = Object.freeze({
  plays: new Map(),
  clearOnNextPlay: false,
});

/** Keep a completed trick visible until the next trick actually starts. */
export function reduceVisibleSeatPlays(
  state: VisibleSeatPlays,
  event: Pick<AuthorityEvent, "type" | "payload">,
): VisibleSeatPlays {
  if (event.type === "CARDS_DEALT") return EMPTY_VISIBLE_SEAT_PLAYS;
  if (event.type === "TRICK_RESET") return { ...state, clearOnNextPlay: true };
  if (event.type !== "CARDS_PLAYED") return state;
  const seat = event.payload.seat;
  const cardIds = event.payload.cardIds;
  const combination = event.payload.combination;
  if (
    typeof seat !== "number" ||
    !Array.isArray(cardIds) ||
    !cardIds.every((cardId) => typeof cardId === "string") ||
    combination === null ||
    typeof combination !== "object" ||
    !("kind" in combination)
  )
    return state;
  const plays = new Map<number, SeatPlay>(
    state.clearOnNextPlay ? [] : state.plays,
  );
  plays.set(seat, {
    seat,
    cardIds,
    combination: combination as NormalizedCombination,
  });
  return { plays, clearOnNextPlay: false };
}

export function shouldAnnounceLowHand(
  previousCount: number | undefined,
  currentCount: number,
  alreadyAnnounced: boolean,
): boolean {
  return (
    !alreadyAnnounced &&
    previousCount !== undefined &&
    previousCount > 10 &&
    currentCount <= 10
  );
}
